import { v4 as uuidv4 } from 'uuid';

import { KnmKindCodesEnum } from 'APIPackage/DictionariesModule/enums/KnmKindCodesEnum';
import { IKnmSubject248 } from 'APIPackage/KnmModule/interfaces/IKnmSubject248';
import { isLaterThanAdminSetting } from 'CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { store } from 'CorePackage/MainModule/common/store';
import { getAdminFetchSettings } from 'PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { convertKnmOkvedsFormValueToKnmData } from 'KnmPackage/MainModule/common/convertKnmOkvedsFormValueToKnmData';
import { getKnmFormRef } from 'KnmPackage/MainModule/selectors/knmSelectors';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';

import { convertActFormValuesToKnmData } from './convertActFormValuesToKnmData';
import { convertResultDecisionsFormValueToKnmData } from './convertResultDecisionsFormValueToKnmData';
import { IKnmErknmForm } from '../interfaces/IKnmErknmForm';
import { IKnmErknmFormSubject } from '../interfaces/IKnmErknmFormSubject';
import { IKnmErknmFormAct } from '../interfaces/IKnmErknmFormAct';
import { IKnmErknmFormResultDecision } from '../interfaces/IKnmErknmFormResultDecision';

const hasOrganizationIdentity = (org?: IKnmErknmFormSubject) =>
  !!(org?.listId || org?.guid || org?.inn || org?.organizationName || org?.ogrn || org?.kpp || org?.rafpNza);

export const convertOrganizationsFormValuesToKnmData = (
  values: IKnmErknmForm,
  created: string | undefined,
  isDraft: boolean | undefined,
): IKnmSubject248[] | null => {
  const { organizations, acts, resultDecisions, kindKnm } = values;
  const isKindKnmCodeRO = kindKnm?.code === KnmKindCodesEnum.RO;
  let formOrganizations: IKnmErknmFormSubject[] = [];
  const state = store.getState();
  const settings = getAdminFetchSettings(state);

  const isEnablePaperlessPassportDocument = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.ENABLED_PAPERLESS_PASSPORT_4_1_3_STARTS_FROM,
  );

  if (organizations && organizations.length > 0) {
    formOrganizations = [...organizations];
  }
  if (isKindKnmCodeRO) {
    acts?.forEach(actsItem => {
      const isActOrganizationAdded = formOrganizations.some(
        formOrganization => formOrganization.listId === actsItem.organization?.listId,
      );

      if (actsItem.organization && !isActOrganizationAdded) {
        formOrganizations.push(actsItem.organization);
      }

      if (
        !isEnablePaperlessPassportDocument &&
        actsItem.typeAct?.id === 2 &&
        actsItem.isNew &&
        !hasOrganizationIdentity(actsItem.organization)
      ) {
        const emptyOrganization: IKnmErknmFormSubject = {
          inn: undefined,
          ogrn: undefined,
          position: undefined,
          okveds: undefined,
          addressFns: undefined,
          organizationName: undefined,
          firstOrganizationName: undefined,
          mspCode: undefined,
          checkSubjectResult: null,
          dateCreate: undefined,
          isAutocomplete: undefined,
          isFiz: undefined,
          guid: undefined,
          isDocumentOrganization: false,
          listId: uuidv4(),
          isNew: true,
          nkoForeignAgentsRegistryNumber: undefined,
          nkoForeignAgentsRegistryInclusionDate: undefined,
          nkoForeignAgentsRegistryExclusionDate: undefined,
          isNko: undefined,
          subjectType: undefined,
          kpp: undefined,
          smevRequestStatus: undefined,
          rafpNza: undefined,
          isForeignSubjectAgencyNotRegistredInRf: undefined,
          country: undefined,
          isSonko: undefined,
          locationAddress: undefined,
          workAddress: undefined,
          factualAddress: undefined,
          snils: undefined,
          document: undefined,
          epguNotifications: undefined,
          ervkId: undefined,
          isPresentInItRegistry: undefined,
          itAccreditationResponse: undefined,
          smevItAccreditationRequestStatus: undefined,
          inspectionResults: undefined,
          isFilledFromLink: false,
          locationregion: undefined,
          locationnotInFIAS: undefined,
          locationmanualAddress: undefined,
          locationfiasRegion: undefined,
        };

        formOrganizations.push(emptyOrganization);
      }
    });
    resultDecisions?.forEach(resultDecisionsItem => {
      const isResultDecisionOrganizationAdded = formOrganizations.some(
        formOrganization => formOrganization.listId === resultDecisionsItem.organization?.listId,
      );

      if (resultDecisionsItem.organization && !isResultDecisionOrganizationAdded) {
        formOrganizations.push(resultDecisionsItem.organization);
      }
    });
  }

  if (formOrganizations.length) {
    return formOrganizations.map(item => {
      const formOrganizationAct: IKnmErknmFormAct | undefined = !isKindKnmCodeRO
        ? acts?.[0]
        : acts?.find(actsItem => {
            if (item.inn || item.organizationName || item.ogrn || item.kpp || item.rafpNza || item.listId) {
              if (actsItem.typeAct?.id === 1 || actsItem.typeAct?.id === 2) {
                return actsItem.organization?.listId === item.listId;
              }

              return isEnablePaperlessPassportDocument && actsItem.organization?.listId === item.listId;
            }

            return actsItem.typeAct?.id === 2 && !hasOrganizationIdentity(actsItem.organization);
          });

      const formOrganizationResultDecisions: IKnmErknmFormResultDecision[] | undefined = !isKindKnmCodeRO
        ? resultDecisions
        : resultDecisions?.filter(resultDecisionsItem => resultDecisionsItem.organization?.listId === item.listId);

      const organizationIndex = organizations?.findIndex(org => org.listId === item.listId);
      const formRef = getKnmFormRef(state);
      /* eslint-disable @typescript-eslint/no-explicit-any */
      const itemAny = item as any;

      /* eslint-disable @typescript-eslint/no-explicit-any */
      const getField = (fieldPath: string): any => {
        if (organizationIndex !== undefined && organizationIndex >= 0 && formRef?.current) {
          const fieldName = `organizations[${organizationIndex}].${fieldPath}` as keyof IKnmForm;
          const fieldState = formRef.current.getFieldState(fieldName);

          if (fieldState?.value !== undefined) {
            return fieldState.value;
          }
          const formValues = formRef.current.getState().values;
          /* eslint-disable @typescript-eslint/no-explicit-any */
          const orgData = (formValues as any)?.organizations?.[organizationIndex];
          const formValue = orgData?.[fieldPath];

          if (formValue !== undefined) {
            return formValue;
          }
        }

        return itemAny[fieldPath];
      };

      const getAddressFields = () => {
        const notInFIAS = getField(`locationnotInFIAS`) ?? itemAny.locationnotInFIAS;

        const address = item[`locationAddress` as keyof typeof item] as string | undefined;

        if (notInFIAS) {
          const region = getField(`locationregion`) ?? itemAny.locationregion;
          const manualAddress = getField(`locationmanualAddress`) ?? itemAny.$locationmanualAddress;

          const regionId = region && 'id' in region && region.id != null ? String(region.id) : null;

          return {
            address: address || null,
            region: regionId,
            manualAddress: manualAddress || null,
            notInFIAS: true,
            fiasRegion: null,
            locationRegionCodeFromFns: item.locationRegionCodeFromFns,
          };
        }
        const fiasRegion = getField(`locationfiasRegion`) ?? itemAny.locationiasRegion;

        const fiasRegionId = fiasRegion && 'id' in fiasRegion && fiasRegion.id != null ? String(fiasRegion.id) : null;

        return {
          address: address || null,
          region: null,
          manualAddress: null,
          notInFIAS: notInFIAS === false ? false : null,
          fiasRegion: fiasRegionId,
          locationRegionCodeFromFns: item.locationRegionCodeFromFns,
        };
      };

      const locationFields = getAddressFields();

      const organization: IKnmSubject248 = {
        isNew: isDraft ? item.isNew : null,
        inn: item.inn || null,
        ogrn: item.ogrn || null,
        position: null,
        okveds: convertKnmOkvedsFormValueToKnmData(item.okveds),
        addressFns: item.addressFns || null,
        organizationName: item.organizationName || null,
        firstOrganizationName: item.firstOrganizationName || null,
        mspCode: item.mspCode || null,
        checkSubjectResult: item.checkSubjectResult || null,
        dateCreate: item.dateCreate || null,
        act: formOrganizationAct ? convertActFormValuesToKnmData(formOrganizationAct, created, values, isDraft) : null,
        resultDecisions: convertResultDecisionsFormValueToKnmData(formOrganizationResultDecisions, created, isDraft),
        isAutocomplete: typeof item.isAutocomplete === 'boolean' ? item.isAutocomplete : null,
        isFiz: typeof item.isFiz === 'boolean' ? item.isFiz : null,
        guid: item.guid || null,
        isDocumentOrganization: item.isDocumentOrganization,
        nkoForeignAgentsRegistryNumber: item.nkoForeignAgentsRegistryNumber || null,
        nkoForeignAgentsRegistryInclusionDate: item.nkoForeignAgentsRegistryInclusionDate || null,
        nkoForeignAgentsRegistryExclusionDate: item.nkoForeignAgentsRegistryExclusionDate || null,
        isNko: !!item.nkoForeignAgentsRegistryInclusionDate,
        subjectType: item.subjectType || null,
        rafpNza: item.rafpNza || null,
        smevRequestStatus: item.smevRequestStatus || null,
        kpp: item.kpp || null,
        isForeignSubjectAgencyNotRegistredInRf: item.isForeignSubjectAgencyNotRegistredInRf || null,
        country: item.country || null,
        isSonko: typeof item.isSonko === 'boolean' ? item.isSonko : null,
        locationAddress: locationFields.address || null,
        workAddress: item.workAddress || null,
        factualAddress: item.factualAddress || null,
        snils: item.snils || null,
        document: item.document || null,
        epguNotifications: item.epguNotifications || null,
        ervkId: item.ervkId || null,
        isPresentInItRegistry: typeof item.isPresentInItRegistry === 'boolean' ? item.isPresentInItRegistry : null,
        itAccreditationResponse: item.itAccreditationResponse || null,
        smevItAccreditationRequestStatus: item.smevItAccreditationRequestStatus || null,
        inspectionResults: item.inspectionResults,
        isFilledFromLink: item.isFilledFromLink || null,
        locationRegion: locationFields.region,
        locationNotInFIAS: typeof locationFields.notInFIAS === 'boolean' ? locationFields.notInFIAS : null,
        locationManualAddress: locationFields.manualAddress,
        locationFiasRegion: locationFields.fiasRegion,
        locationRegionCodeFromFns: locationFields.locationRegionCodeFromFns || null,
      };

      return organization;
    });
  }

  return null;
};
